
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import Loader from '@/common/components/UI/Loader/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

import useAccount from '@/common/composable/useAccount'
import useNotifications from '@/common/composable/useNotifications'

import { requiredIf } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

import { DataState } from '@/core/classes/DataState.class'

import { ContractRegister, ContractStatusT } from '@/core/types/contracts.types'

import { contracts } from '@/core/realisations/contracts.realisation'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        Loader,
        PageTitle,
        Cheap,
        TheButton,
        ButtonDropdown,
        TheSelect,
        TheInput,
        Modal,
    },
    setup() {
        const router = useRouter()
        const route = useRoute()
        const notifications = useNotifications()

        const { havePermissions } = useAccount()

        const contractId = Number(route.params.contract_id)

        const state = reactive({
            is_loading: true,
            is_edit_contract: false,
            contract: null as ContractRegister | null,
        })

        const state_modal = reactive({
            is_open: false,
            is_status: false,
            is_comment: false,
            is_loading: false,
            //
            statusId: null as number | null,
            comment: null as string | null,
        })
        const isCancelStatus = computed(() => state_modal.statusId === 4)
        const isComment = computed(
            () => (state_modal.is_status && isCancelStatus.value) || state_modal.is_comment
        )

        const state_modal$ = useVuelidate(
            {
                statusId: { required: requiredIf(() => state_modal.is_status) },
                comment: {
                    required: requiredIf(() => isComment.value),
                },
            },
            state_modal
        )

        const data_state = reactive({
            statuses: new DataState<ContractStatusT>({}),
        })

        const loadContract = async (contract_id: number) => {
            try {
                state.is_loading = true
                const response = await contracts.getContractRegister(contract_id)
                state.contract = response.data
            } catch (error) {
                console.error(error)
            } finally {
                state.is_loading = false
            }
        }

        const goRegister = () => router.push('/contracts/register')
        const goEdit = () => router.push(`/contracts/register/edit/${contractId}`)

        const getStatusColor = (id: number) => {
            if (id === 4) return 'yellow'
            if ([5, 7, 8].includes(id)) return 'green'
            return 'yellow'
        }

        const cancelModal = () => {
            state_modal.is_open = false
            state_modal.is_comment = false
            state_modal.is_status = false

            state_modal.statusId = null
            state_modal.comment = null
        }

        const notifyMessage = () => {
            if (state_modal.is_comment) {
                notifications.notify({
                    title: 'Комментарий успешно добавлен!',
                    type: 'success',
                })
            }

            if (state_modal.is_status) {
                notifications.notify({
                    title: 'Статус успешно изменен!',
                    type: 'success',
                })
            }
        }

        const confirmModal = async () => {
            state_modal$.value.$touch()
            if (state_modal$.value.$invalid) return

            try {
                state_modal.is_loading = true

                if (state_modal.is_comment) {
                    await contracts.addCommentRegister(contractId, state_modal.comment as string)
                    notifyMessage()
                }

                if (state_modal.is_status) {
                    await contracts.changeStatusRegister(
                        contractId,
                        state_modal.statusId as number,
                        state_modal.comment as string
                    )
                    notifyMessage()
                }

                cancelModal()
                loadContract(contractId)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state_modal.is_loading = false
            }
        }

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true
                const response = await contracts.getStatusesRegister()
                data_state.statuses.list = response.data

                data_state.statuses.is_failed = false
                data_state.statuses.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.statuses.failure.catchError(error)

                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_failed = true
                }
            } finally {
                if (!data_state.statuses.failure.error_state.is_cancel) {
                    data_state.statuses.is_loading = false
                }
            }
        }

        const onComment = () => {
            state_modal.is_open = true
            state_modal.is_comment = true
            loadStatuses()
        }

        const onStatus = () => {
            state_modal.is_open = true
            state_modal.is_status = true
            loadStatuses()
        }

        const getModalTitle = computed(() => {
            if (state_modal.is_open) {
                if (state_modal.is_comment) return 'Добавить комментарий'
                if (state_modal.is_status) return 'Изменить статус'
            }

            return ''
        })

        onBeforeMount(() => {
            loadContract(contractId)
        })

        return {
            state,
            state_modal,
            state_modal$,
            data_state,
            goRegister,
            goEdit,
            loadContract,
            havePermissions,
            getStatusColor,
            contractId,
            getModalTitle,
            cancelModal,
            confirmModal,
            onComment,
            onStatus,
            loadStatuses,
            isComment,
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
        }
    },
})
